import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import {
    DotLegalBreadCrumbModel,
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalTableWithControls,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { PredefinedRiskAreaViewModel, RiskAreaRow, RiskAreaViewModel } from "./RiskAreas.types";
import { toSaveModel, useRiskAreasDataMapping } from "./RiskAreas.hooks";
import React, { useState } from "react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useRiskAreaStyles } from "./RiskAreas.styles";
import RiskAreaDialog from "../riskAreaDialog/RiskAreaDialog";
import { RiskAreaSaveModel } from "../riskAreaDialog/RiskAreaDialog.types";
import { createDefaultRiskAreaModel, defaultScale } from "../riskAreaDialog/RiskAreaDialog.hooks";
import SelectRiskAreaTemplateDialog from "../predefinedRiskAreas/SelectRiskAreaTemplateDialog";
import { DeleteRiskAreaDialog } from "../deleteRiskAreaDialog/DeleteRiskAreaDialog";
import Rocket from "../../../processingActivity/processingActivities/rocket.svg?react";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function RiskAreas() {
    const styles = useRiskAreaStyles();
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const breadCrumbs: Array<DotLegalBreadCrumbModel> = [{ name: translateString("riskAreas") }];

    const {
        isLoading,
        data,
        hasRiskAreas,
        showTemplateDialog,
        setShowTemplateDialog,
        showRiskAreaDialog,
        setShowRiskAreaDialog,
        riskAreaForDeletion,
        setRiskAreaForDeletion,
        onSave,
        selectedRiskArea,
        setSelectedRiskArea,
    } = useRiskAreasDataMapping();

    // The predefined picker and the config dialog are separate dialogs; animating the swap between them
    // flashes the page through the backdrop gap. Opening the config dialog instantly (no transition) only
    // on the picker path avoids that flicker while leaving the edit/row-click open animations untouched.
    const [openConfigWithoutTransition, setOpenConfigWithoutTransition] = useState(false);

    const openConfigDialogForExisting = (riskArea: RiskAreaViewModel) => {
        setOpenConfigWithoutTransition(false);
        setSelectedRiskArea(toSaveModel(riskArea));
        setShowRiskAreaDialog(true);
    };

    const getMenuItems = (riskArea: RiskAreaViewModel) => {
        let menuItems = [];

        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => openConfigDialogForExisting(riskArea),
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setRiskAreaForDeletion(riskArea);
                },
            });
        }

        return menuItems;
    };

    const openTemplateDialog = () => {
        setShowTemplateDialog(true);
    };

    const openConfigDialog = (selected: RiskAreaSaveModel | undefined) => {
        setOpenConfigWithoutTransition(true);
        setSelectedRiskArea(selected);
        setShowTemplateDialog(false);
        setShowRiskAreaDialog(true);
    };

    const onSelectPredefined = (predefinedRiskArea: PredefinedRiskAreaViewModel) => {
        openConfigDialog({ ...createDefaultRiskAreaModel(defaultScale, translateString), name: predefinedRiskArea.name });
    };

    return (
        <Box sx={styles.container}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    permissions.canManageMasterData ? (
                        <DotLegalButton buttonType={"primary"} size="medium" onClick={openTemplateDialog}>
                            {translateString("createRiskArea")}
                        </DotLegalButton>
                    ) : undefined
                }
            />

            <TablePagePaper>
                {hasRiskAreas ? (
                    <DotLegalTableWithControls
                        internalScroll
                        headers={getHeaders(translateString)}
                        getUserSpecificPageLength={() => 30}
                        hideRowsPerPage
                        hasOverflowMenu
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        clickableRows={permissions.canManageMasterData}
                        labelRowsPerPage={translateString("showEntites")}
                        labelSearch={translateString("search")}
                        noOptionsLabel={translateString("noOptions")}
                        paginationLabelOf={translateString("labelOf")}
                        emptyText={translateString("noRiskAreasMatchesQuery")}
                        data={data}
                        renderRow={(row, i) => (
                            <TableRow onClick={permissions.canManageMasterData ? () => openConfigDialogForExisting(row) : undefined} key={row.id}>
                                <TableCell sx={styles.bold}>{row.name}</TableCell>
                                <TableCell>{row.responsibleName}</TableCell>
                                <TableCell>{row.assessmentLevelName}</TableCell>
                                <TableCell>{`1-${row.scale}`}</TableCell>
                                <TableCell align="right">{row.appetite}</TableCell>
                                <TableCell align="right">{row.assessmentCount}</TableCell>
                                <TableCell align={"right"}>
                                    <DotLegalOverflowMenu items={getMenuItems(row)} />
                                </TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<Rocket />} text={translateString("riskAreasEmptyState")} />
                )}
            </TablePagePaper>

            {showTemplateDialog && (
                <SelectRiskAreaTemplateDialog
                    onSelectPredefined={onSelectPredefined}
                    onSelectCustom={() => openConfigDialog(undefined)}
                    onCloseDialog={() => setShowTemplateDialog(false)}
                />
            )}
            {showRiskAreaDialog && (
                <RiskAreaDialog
                    onHideDialog={() => setShowRiskAreaDialog(false)}
                    onSave={onSave}
                    selectedRiskArea={selectedRiskArea}
                    disableTransition={openConfigWithoutTransition}
                />
            )}
            {riskAreaForDeletion && (
                <DeleteRiskAreaDialog
                    riskAreaName={riskAreaForDeletion.name}
                    riskAreaId={riskAreaForDeletion.id}
                    onDeletion={onSave}
                    onCloseDialog={() => setRiskAreaForDeletion(undefined)}
                />
            )}
        </Box>
    );
}

export default RiskAreas;

function getHeaders(translateString: (key: string) => string) {
    let headers = Array<ITableHeader<RiskAreaRow>>();
    headers.push({ property: "name", text: translateString("riskAreaName"), align: "left", showOnMobile: true });
    headers.push({ property: "responsibleName", text: translateString("responsible"), align: "left", showOnMobile: true });
    headers.push({ property: "assessmentLevelName", text: translateString("riskAreaAssessmentLevel"), align: "left", showOnMobile: false });
    headers.push({ property: "scale", text: translateString("riskAreaScale"), align: "left", showOnMobile: true });
    headers.push({ property: "appetite", text: translateString("riskAppetite"), align: "left", showOnMobile: true });
    headers.push({ property: "assessmentCount", text: translateString("riskAssessments"), align: "left", showOnMobile: false });

    return headers;
}
